/**
 * Device Type Multi-Select Component - Creatable
 * Supports both predefined options (P12, Amit, VPOST) AND manual text entry
 * Users can select from common types or type custom device types
 */

import { useState, useCallback, KeyboardEvent } from 'react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Checkbox } from '@/core/components/ui/checkbox';
import { Label } from '@/core/components/ui/label';
import { Badge } from '@/core/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/core/components/ui/popover';
import { ChevronDown, X } from 'lucide-react';
import { COMMON_DEVICE_TYPES, DeviceTypeLabels } from '../../types/filter-enums';

interface DeviceTypeMultiSelectProps {
  value: string[];  // Array of strings (both predefined and custom)
  onChange: (value: string[]) => void;
  onAutoRefresh?: () => void;
  className?: string;
}

export function DeviceTypeMultiSelect({
  value,
  onChange,
  onAutoRefresh,
  className = '',
}: DeviceTypeMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleToggle = useCallback(
    (deviceType: string) => {
      let newValue: string[];
      if (value.includes(deviceType)) {
        newValue = value.filter((v) => v !== deviceType);
      } else {
        newValue = [...value, deviceType];
      }
      onChange(newValue);
      if (onAutoRefresh) {
        onAutoRefresh();
      }
    },
    [value, onChange, onAutoRefresh]
  );

  const handleAddCustom = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
      if (onAutoRefresh) {
        onAutoRefresh();
      }
    }
  }, [inputValue, value, onChange, onAutoRefresh]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddCustom();
      }
    },
    [handleAddCustom]
  );

  const handleRemove = useCallback(
    (deviceType: string) => {
      onChange(value.filter((v) => v !== deviceType));
      if (onAutoRefresh) {
        onAutoRefresh();
      }
    },
    [value, onChange, onAutoRefresh]
  );

  const handleClearAll = useCallback(() => {
    onChange([]);
    if (onAutoRefresh) {
      onAutoRefresh();
    }
  }, [onChange, onAutoRefresh]);

  const handleSelectAllCommon = useCallback(() => {
    // Add all common types that aren't already selected
    const newTypes = [...COMMON_DEVICE_TYPES].filter(t => !value.includes(t));
    onChange([...value, ...newTypes]);
    if (onAutoRefresh) {
      onAutoRefresh();
    }
  }, [value, onChange, onAutoRefresh]);

  const getDisplayText = () => {
    if (value.length === 0) return 'All';
    if (value.length === 1) return value[0];
    return `${value.length} selected`;
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <Label className="text-xs text-muted-foreground">Device Type</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 justify-between text-sm font-normal"
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          {/* Input for custom device types */}
          <div className="p-2 border-b">
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                placeholder="Enter custom type..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-7 text-sm"
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={handleAddCustom}
                disabled={!inputValue.trim()}
              >
                Add
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleSelectAllCommon}
              >
                Select Common
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleClearAll}
              >
                Clear All
              </Button>
            </div>
          </div>

          {/* Common predefined options */}
          <div className="p-2 border-b">
            <div className="text-xs text-muted-foreground mb-1 px-1">Common Types:</div>
            <div className="space-y-1">
              {COMMON_DEVICE_TYPES.map((deviceType) => (
                <div
                  key={deviceType}
                  className="flex items-center gap-2 py-1 px-1 hover:bg-muted rounded cursor-pointer"
                  onClick={() => handleToggle(deviceType)}
                >
                  <Checkbox
                    checked={value.includes(deviceType)}
                    onCheckedChange={() => handleToggle(deviceType)}
                  />
                  <span className="text-sm">{DeviceTypeLabels[deviceType] || deviceType}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Selected values (chips) */}
          {value.length > 0 && (
            <div className="p-2">
              <div className="text-xs text-muted-foreground mb-1">Selected ({value.length}):</div>
              <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto">
                {value.map((deviceType) => (
                  <Badge
                    key={deviceType}
                    variant="secondary"
                    className="text-xs py-0.5 px-2 pr-1"
                  >
                    {deviceType}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(deviceType);
                      }}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
